import React from 'react';
import { useNavigate } from 'react-router-dom';

const isPromoActive = (product) => {
  if (!product.promoPriceCents) return false;
  const now = new Date();
  if (product.promoStartDate) {
    const start = new Date(product.promoStartDate);
    if (now < start) return false;
  }
  if (product.promoEndDate) {
    const end = new Date(product.promoEndDate + 'T23:59:59');
    if (now > end) return false;
  }
  return true;
};

const ProductCard = ({ product, slug, accentColor, cardBg, isDark, formatPrice, onWhatsApp, shopName }) => {
  const navigate = useNavigate();
  const hasPromo = isPromoActive(product);
  const firstImage = product.images?.[0];
  const API = process.env.REACT_APP_BACKEND_URL;

  return (
    <div
      className="rounded-xl overflow-hidden border cursor-pointer transition-all hover:shadow-md"
      style={{
        backgroundColor: cardBg,
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
      }}
      onClick={() => navigate(`/s/${slug}/p/${product.id}`)}
      data-testid={`public-product-card-${product.id}`}
    >
      <div className="p-4 flex gap-4">
        {/* Image thumbnail */}
        {firstImage && (
          <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-gray-100">
            <img
              src={`${API}${firstImage.url}`}
              alt={product.name}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold line-clamp-1">{product.name}</h3>
            {/* Price */}
            {hasPromo ? (
              <div className="text-right shrink-0">
                <span className="text-sm line-through opacity-50">{formatPrice(product.priceCents)}</span>
                <span className="block font-bold text-red-500" data-testid={`public-promo-price-${product.id}`}>
                  {formatPrice(product.promoPriceCents)}
                </span>
              </div>
            ) : (
              product.priceCents && (
                <span className="font-bold shrink-0" style={{ color: accentColor }}>
                  {formatPrice(product.priceCents)}
                </span>
              )
            )}
          </div>

          {product.description && (
            <p className="text-sm opacity-60 line-clamp-1 mt-0.5">{product.description}</p>
          )}

          <div className="flex items-center gap-2 mt-2">
            {product.isFeatured && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${accentColor}20`, color: accentColor }}>
                Vedette
              </span>
            )}
            {hasPromo && (
              <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-red-500 text-white" data-testid={`public-promo-badge-${product.id}`}>
                PROMO
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 pb-3 flex items-center justify-between">
        <button
          className="text-sm font-medium flex items-center gap-1"
          style={{ color: accentColor }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/s/${slug}/p/${product.id}`);
          }}
        >
          Voir les détails
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            const price = hasPromo ? formatPrice(product.promoPriceCents) : formatPrice(product.priceCents);
            const url = `${window.location.origin}/s/${slug}/p/${product.id}`;
            const msg = `${product.name}${price ? ` - ${price}` : ''}${shopName ? ` chez ${shopName}` : ''}\n${url}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
          }}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          style={{ backgroundColor: '#25D36618', color: '#25D366' }}
          data-testid={`share-whatsapp-${product.id}`}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
